import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Shield, TrendingUp, DollarSign, Clock, ChevronRight,
  Lock, FileText, PenTool, Building2, AlertTriangle, CheckCircle,
  Banknote, BarChart3, Calendar, Send, ArrowRight, Eye, X,
  Wallet, PieChart
} from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, MetricPill, SectionHeader, DotLeaderRow, DotLeaderTable } from "../shared/DesignSystem";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtUSD(v) {
  if (!v && v !== 0) return "$0";
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
}

// ── Waitlist Banner ──────────────────────────────────────────────

export function WaitlistBanner({ status, onJoin }) {
  if (!status || status.fund_platform_enabled) return null;
  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 p-5 mb-6"
      data-testid="waitlist-banner">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-xl bg-amber-200/60">
          <Clock className="h-5 w-5 text-amber-700" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-amber-900">Fund Launch Coming Soon</h3>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">{status.waitlist_message}</p>
          {status.launch_date && (
            <p className="text-xs text-amber-600 mt-2 font-medium">Expected Launch: {status.launch_date}</p>
          )}
          <button onClick={onJoin}
            className="mt-3 px-4 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition-colors"
            data-testid="join-waitlist-btn">
            Join Waitlist
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Fund Opportunity Card ────────────────────────────────────────

export function FundOpportunityCard({ opp, onViewMemo, onSubscribe }) {
  const pctFunded = opp.target_raise > 0 ? Math.min(100, Math.round(opp.raised_amount / opp.target_raise * 100)) : 0;
  const riskLabel = opp.risk_profile || "Moderate";

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="ic-deal-card"
      data-testid={`fund-card-${opp.opportunity_id}`}>

      {/* Fund Header */}
      <div style={{
        height: 100, background: "var(--ic-green-dark)", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: "0 16px",
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ic-text-inverse)", textAlign: "center", lineHeight: 1.3 }}>
          {opp.title || "Investment Fund"}
        </div>
        <div style={{ fontSize: 13, color: "var(--ic-gold)", marginTop: 4 }}>
          {opp.fund_type === "private_credit" ? "Private Credit" : opp.fund_type || "Private Credit"}
        </div>
      </div>

      {/* Card Body */}
      <div className="ic-deal-card-body">
        {/* MetricPills: YIELD / TERM / MIN */}
        <div className="ic-deal-card-pills">
          <MetricPill label="Yield" value={opp.target_yield_pct ? `${opp.target_yield_pct}%` : "—"} highlight />
          <MetricPill label="Term" value={opp.projected_term_months ? `${opp.projected_term_months} mo` : "—"} />
          <MetricPill label="Min" value={fmtUSD(opp.min_investment)} />
        </div>

        {/* Raise Progress */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: "var(--ic-text-secondary)" }}>{fmtUSD(opp.raised_amount)} raised</span>
            <span style={{ color: "var(--ic-text-secondary)" }}>of {fmtUSD(opp.target_raise)}</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: "var(--ic-border)", overflow: "hidden" }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pctFunded}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="ic-progress-bar-fill"
              style={{ height: "100%", borderRadius: 3, background: "var(--ic-green-light)" }}
            />
          </div>
          <div style={{ fontSize: 12, color: "var(--ic-text-secondary)", marginTop: 4 }}>
            {opp.investor_count} investor{opp.investor_count !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Risk Profile Badge */}
        <div style={{ marginBottom: 14 }}>
          <StatusBadge status={riskLabel} />
        </div>

        {/* Blurred overlay for unverified */}
        {opp.is_blurred ? (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            padding: 12, borderRadius: "var(--ic-radius)", background: "var(--ic-bg-row-alt)",
            border: "1px solid var(--ic-border)", color: "var(--ic-text-secondary)", fontSize: 13,
          }}>
            <Lock className="h-4 w-4" /> Complete accreditation to view details
          </div>
        ) : (
          <div className="ic-deal-card-buttons">
            {opp.my_commitment ? (
              <span className="ic-btn-outline" style={{ flex: 1, cursor: "default", borderColor: "var(--ic-green-light)", color: "var(--ic-green-light)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <CheckCircle className="h-3.5 w-3.5" /> Subscribed ({fmtUSD(opp.my_commitment.amount)})
              </span>
            ) : opp.status === "open" ? (
              <>
                <button onClick={() => onSubscribe(opp)} className="ic-btn-primary" data-testid={`subscribe-${opp.opportunity_id}`}>
                  <DollarSign className="h-3.5 w-3.5 inline mr-1" /> Invest
                </button>
                <button onClick={() => onViewMemo(opp)} className="ic-btn-outline" data-testid={`view-memo-${opp.opportunity_id}`}>
                  Learn More
                </button>
              </>
            ) : (
              <button onClick={() => onViewMemo(opp)} className="ic-btn-outline" style={{ flex: 1 }} data-testid={`view-memo-${opp.opportunity_id}`}>
                Learn More
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── Fund Memo Modal ──────────────────────────────────────────────

export function FundMemoModal({ opp, onClose }) {
  const [memo, setMemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ic_token");

  useEffect(() => {
    axios.get(`${API}/fund-portal/opportunities/${opp.opportunity_id}/memo`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setMemo(r.data)).catch(e => toast.error(e.response?.data?.detail || "Failed to load memo"))
      .finally(() => setLoading(false));
  }, [opp.opportunity_id]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()} data-testid="fund-memo-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#e8e0d0] bg-gradient-to-r from-emerald-50 to-white flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-[#0A0A0A]">{opp.title}</h3>
            <p className="text-xs text-[#888]">Investor Memo</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#faf7f2] text-[#888]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
            </div>
          ) : memo ? (
            <div className="space-y-6">
              {/* Key metrics bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Target Yield", value: `${memo.target_yield_pct || "—"}%`, icon: TrendingUp, color: "emerald" },
                  { label: "Fund Size", value: fmtUSD(memo.target_raise), icon: DollarSign, color: "blue" },
                  { label: "Term", value: `${memo.projected_term_months || "—"}mo`, icon: Clock, color: "amber" },
                  { label: "Raised", value: fmtUSD(memo.raised_amount), icon: BarChart3, color: "purple" },
                ].map(m => (
                  <div key={m.label} className="text-center p-3 rounded-xl bg-[#FDFCF9] border border-[#f0ece4]">
                    <m.icon className={`h-4 w-4 mx-auto mb-1 text-${m.color}-600`} />
                    <p className="text-sm font-bold text-[#0A0A0A]">{m.value}</p>
                    <p className="text-[9px] text-[#888] uppercase tracking-wider">{m.label}</p>
                  </div>
                ))}
              </div>

              {/* Fund details */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                <div className="flex justify-between py-1.5 border-b border-[#f0ece4]">
                  <span className="text-[#888]">Fund Type</span>
                  <span className="font-medium text-[#333]">{memo.fund_type === "private_credit" ? "Private Credit" : memo.fund_type}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[#f0ece4]">
                  <span className="text-[#888]">Risk Profile</span>
                  <span className="font-medium text-[#333] capitalize">{memo.risk_profile}</span>
                </div>
                {memo.management_fee_pct != null && (
                  <div className="flex justify-between py-1.5 border-b border-[#f0ece4]">
                    <span className="text-[#888]">Mgmt Fee</span>
                    <span className="font-medium text-[#333]">{memo.management_fee_pct}%</span>
                  </div>
                )}
                {memo.performance_fee_pct != null && (
                  <div className="flex justify-between py-1.5 border-b border-[#f0ece4]">
                    <span className="text-[#888]">Performance Fee</span>
                    <span className="font-medium text-[#333]">{memo.performance_fee_pct}%</span>
                  </div>
                )}
                <div className="flex justify-between py-1.5 border-b border-[#f0ece4]">
                  <span className="text-[#888]">Min Investment</span>
                  <span className="font-medium text-[#333]">{fmtUSD(memo.min_investment)}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[#f0ece4]">
                  <span className="text-[#888]">Investors</span>
                  <span className="font-medium text-[#333]">{memo.investor_count}</span>
                </div>
              </div>

              {/* Strategy */}
              {memo.strategy_summary && (
                <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
                  <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-blue-700 mb-2">Investment Strategy</p>
                  <p className="text-xs text-blue-800 leading-relaxed">{memo.strategy_summary}</p>
                </div>
              )}

              {/* Memo content */}
              {memo.memo_html && (
                <div className="prose prose-sm max-w-none text-[#333]"
                  dangerouslySetInnerHTML={{ __html: memo.memo_html }} />
              )}
            </div>
          ) : (
            <p className="text-sm text-[#888] text-center py-8">No memo available for this opportunity.</p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Subscription Flow Modal (3-step) ─────────────────────────────

export function SubscriptionModal({ opp, onClose, onSuccess }) {
  const [step, setStep] = useState("details"); // details → signature → wire → confirmed
  const [amount, setAmount] = useState("");
  const [commitmentId, setCommitmentId] = useState(null);
  const [agreementHtml, setAgreementHtml] = useState("");
  const [wireHtml, setWireHtml] = useState("");
  const [signature, setSignature] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const handleStep1 = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < (opp.min_investment || 0)) {
      setError(`Minimum investment is ${fmtUSD(opp.min_investment || 0)}`);
      return;
    }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/fund-portal/subscribe/start`, {
        opportunity_id: opp.opportunity_id, amount: amt,
      }, { headers });
      setCommitmentId(res.data.commitment_id);
      setAgreementHtml(res.data.agreement_html);
      setStep("signature");
    } catch (err) { setError(err.response?.data?.detail || "Failed to start subscription"); }
    finally { setLoading(false); }
  };

  const handleStep2 = async () => {
    if (!signature.trim() || signature.trim().length < 2) {
      setError("Please type your full legal name as your electronic signature.");
      return;
    }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/fund-portal/subscribe/sign`, {
        commitment_id: commitmentId, electronic_signature: signature.trim(),
      }, { headers });
      setWireHtml(res.data.wire_instructions_html);
      setStep("wire");
    } catch (err) { setError(err.response?.data?.detail || "Failed to sign"); }
    finally { setLoading(false); }
  };

  const handleStep3 = async () => {
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/fund-portal/subscribe/complete`, {
        commitment_id: commitmentId,
      }, { headers });
      setStep("confirmed");
      toast.success("Subscription submitted successfully!");
      setTimeout(() => onSuccess(), 2000);
    } catch (err) { setError(err.response?.data?.detail || "Failed to complete"); }
    finally { setLoading(false); }
  };

  const stepLabels = [
    { key: "details", label: "Fund Details", num: 1 },
    { key: "signature", label: "Sign Agreement", num: 2 },
    { key: "wire", label: "Wire Instructions", num: 3 },
  ];
  const currentStepIdx = stepLabels.findIndex(s => s.key === step);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()} data-testid="subscription-modal">

        {/* Header */}
        <div className="px-5 py-4 border-b border-[#e8e0d0] bg-gradient-to-r from-emerald-50 to-white flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-sm font-bold text-[#0A0A0A]">Fund Subscription</h3>
            <p className="text-[10px] text-[#888]">{opp.title}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#faf7f2] text-[#888]"><X className="h-4 w-4" /></button>
        </div>

        {/* Step indicator */}
        {step !== "confirmed" && (
          <div className="px-5 py-3 border-b border-[#f0ece4] flex items-center gap-2">
            {stepLabels.map((s, i) => (
              <React.Fragment key={s.key}>
                <div className={`flex items-center gap-1.5 ${i <= currentStepIdx ? "text-emerald-700" : "text-[#ccc]"}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i <= currentStepIdx ? "bg-emerald-600 text-white" : "bg-[#eee] text-[#999]"}`}>{s.num}</span>
                  <span className="text-[10px] font-medium hidden sm:inline">{s.label}</span>
                </div>
                {i < stepLabels.length - 1 && <div className={`flex-1 h-px ${i < currentStepIdx ? "bg-emerald-400" : "bg-[#eee]"}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="overflow-y-auto flex-1 p-5">
          {step === "confirmed" ? (
            <div className="text-center py-8">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 12 }}>
                <CheckCircle className="h-14 w-14 mx-auto text-emerald-600 mb-3" />
              </motion.div>
              <p className="font-bold text-lg text-[#0A0A0A]">Subscription Submitted</p>
              <p className="text-xs text-[#888] mt-1">You'll receive a confirmation once your wire is received.</p>
            </div>
          ) : step === "details" ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-[#FDFCF9] border border-[#f0ece4] p-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-base font-bold text-emerald-700">{opp.target_yield_pct || "—"}%</p>
                    <p className="text-[9px] text-[#888] uppercase">Target Yield</p>
                  </div>
                  <div>
                    <p className="text-base font-bold text-[#0A0A0A]">{opp.projected_term_months || "—"}mo</p>
                    <p className="text-[9px] text-[#888] uppercase">Term</p>
                  </div>
                  <div>
                    <p className="text-base font-bold text-[#0A0A0A]">{fmtUSD(opp.min_investment)}</p>
                    <p className="text-[9px] text-[#888] uppercase">Minimum</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#444] mb-1.5">Subscription Amount ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#bbb]" />
                  <input type="number" value={amount} onChange={e => { setAmount(e.target.value); setError(""); }}
                    placeholder={`Minimum ${fmtUSD(opp.min_investment || 0)}`}
                    className="w-full pl-9 pr-4 py-3 rounded-xl border border-[#e8e0d0] bg-white text-sm text-[#0A0A0A] placeholder:text-[#ccc] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
                    data-testid="subscription-amount-input" />
                </div>
              </div>
              {error && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{error}</p>}
              <button onClick={handleStep1} disabled={loading}
                className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="subscription-continue-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRight className="h-4 w-4" /> Continue to Agreement</>}
              </button>
            </div>
          ) : step === "signature" ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-[#e8e0d0] bg-[#FDFCF9] p-4 max-h-48 overflow-y-auto text-xs text-[#555] leading-relaxed prose prose-sm"
                dangerouslySetInnerHTML={{ __html: agreementHtml }} />
              <div>
                <label className="block text-xs font-bold text-[#444] mb-1.5">
                  <PenTool className="inline h-3 w-3 mr-1" />Electronic Signature — Type your full legal name
                </label>
                <input type="text" value={signature} onChange={e => { setSignature(e.target.value); setError(""); }}
                  placeholder="Your full legal name"
                  className="w-full px-4 py-3 rounded-xl border border-[#e8e0d0] bg-white text-sm italic font-serif text-[#0A0A0A] placeholder:text-[#ccc] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
                  data-testid="electronic-signature-input" />
              </div>
              <p className="text-[10px] text-[#999] leading-relaxed">
                By typing your name above and clicking "Sign & Continue", you are electronically signing this subscription agreement
                pursuant to the Electronic Signatures in Global and National Commerce Act (E-SIGN).
              </p>
              {error && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{error}</p>}
              <button onClick={handleStep2} disabled={loading}
                className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="sign-agreement-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PenTool className="h-4 w-4" /> Sign & Continue</>}
              </button>
            </div>
          ) : step === "wire" ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-xs text-blue-800 leading-relaxed prose prose-sm"
                dangerouslySetInnerHTML={{ __html: wireHtml }} />
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2">
                <Banknote className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <p className="text-xs text-emerald-700">
                  Your subscription amount: <strong>{fmtUSD(parseFloat(amount))}</strong>
                </p>
              </div>
              {error && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{error}</p>}
              <button onClick={handleStep3} disabled={loading}
                className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="complete-subscription-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4" /> I've Noted the Wire Instructions — Complete Subscription</>}
              </button>
            </div>
          ) : null}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Portfolio Summary Panel ──────────────────────────────────────

export function FundPortfolioPanel() {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [taxData, setTaxData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/fund-portal/portfolio/summary`, { headers }),
      axios.get(`${API}/fund-portal/portfolio/timeline`, { headers }),
      axios.get(`${API}/fund-portal/portfolio/tax-summary`, { headers }),
    ]).then(([s, t, tx]) => {
      setData(s.data);
      setTimeline(t.data.events || []);
      setTaxData(tx.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 text-emerald-600 animate-spin" /></div>;
  if (!data) return null;

  const tabs = [
    { key: "summary", label: "Summary", icon: PieChart },
    { key: "timeline", label: "Timeline", icon: Clock },
    { key: "tax", label: "Tax Summary", icon: FileText },
  ];

  return (
    <div style={{ borderRadius: "var(--ic-radius-lg)", border: "1px solid var(--ic-border)", background: "var(--ic-bg-card)", overflow: "hidden", boxShadow: "var(--ic-shadow-sm)" }} data-testid="fund-portfolio-panel">
      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--ic-border)" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "12px 0", fontSize: 12, fontWeight: 700, transition: "all 0.15s ease",
              border: "none", borderBottom: activeTab === t.key ? "2px solid var(--ic-gold)" : "2px solid transparent",
              background: activeTab === t.key ? "rgba(212,175,55,0.05)" : "transparent",
              color: activeTab === t.key ? "var(--ic-green-dark)" : "var(--ic-text-secondary)",
              cursor: "pointer",
            }}
            data-testid={`portfolio-tab-${t.key}`}>
            <t.icon style={{ width: 14, height: 14 }} /> {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: 20 }}>
        {activeTab === "summary" && (
          <div className="space-y-4">
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Committed", value: fmtUSD(data.total_committed), icon: DollarSign },
                { label: "Avg Yield", value: data.weighted_avg_yield ? `${data.weighted_avg_yield}%` : "—", icon: TrendingUp },
                { label: "Distrib.", value: fmtUSD(data.total_distributions), icon: Banknote },
                { label: "Net P&L", value: fmtUSD(data.net_pnl), icon: BarChart3 },
              ].map(kpi => (
                <div key={kpi.label} style={{ padding: 12, borderRadius: "var(--ic-radius)", background: "var(--ic-bg-row-alt)", border: "1px solid var(--ic-border)" }} data-testid={`portfolio-kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <kpi.icon style={{ width: 12, height: 12, color: "var(--ic-text-secondary)" }} />
                    <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ic-text-secondary)", fontWeight: 700 }}>{kpi.label}</span>
                  </div>
                  <p style={{ fontSize: 18, fontWeight: 700, color: "var(--ic-text-primary)" }}>{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* Status breakdown */}
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { label: "Active", value: data.active_subscriptions, color: "var(--ic-status-open)", bg: "rgba(64,145,108,0.1)" },
                { label: "Funded", value: data.funded_subscriptions, color: "var(--ic-status-funded)", bg: "rgba(27,67,50,0.1)" },
                { label: "Pending", value: data.pending_subscriptions, color: "var(--ic-status-pending)", bg: "rgba(217,119,6,0.1)" },
              ].map(s => (
                <div key={s.label} style={{ padding: 8, borderRadius: "var(--ic-radius)", background: s.bg, border: "1px solid var(--ic-border)" }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.value}</p>
                  <p style={{ fontSize: 9, color: s.color }}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Fund breakdown */}
            {Object.keys(data.fund_breakdown).length > 0 && (
              <div>
                <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--ic-text-secondary)", fontWeight: 700, marginBottom: 8 }}>By Fund</p>
                {Object.entries(data.fund_breakdown).map(([name, info]) => (
                  <div key={name} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dotted var(--ic-border)" }}>
                    <span style={{ fontSize: 12, color: "var(--ic-text-primary)", fontWeight: 500 }}>{name}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ic-text-primary)" }}>{fmtUSD(info.committed)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "timeline" && (
          <div className="space-y-3">
            {timeline.length === 0 ? (
              <p className="text-sm text-[#888] text-center py-6">No activity yet.</p>
            ) : timeline.map((ev, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-[#f0ece4] last:border-0">
                <div className={`p-1.5 rounded-lg ${ev.type === "distribution" ? "bg-emerald-100" : ev.type === "funded" ? "bg-blue-100" : ev.type === "signature" ? "bg-purple-100" : "bg-amber-100"}`}>
                  {ev.type === "distribution" ? <Banknote className="h-3 w-3 text-emerald-600" /> :
                    ev.type === "funded" ? <CheckCircle className="h-3 w-3 text-blue-600" /> :
                    ev.type === "signature" ? <PenTool className="h-3 w-3 text-purple-600" /> :
                    <DollarSign className="h-3 w-3 text-amber-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-[#333]">{ev.title}</p>
                  {ev.amount != null && <p className="text-[10px] text-[#888]">{fmtUSD(ev.amount)}</p>}
                  <p className="text-[10px] text-[#bbb]">{ev.date ? new Date(ev.date).toLocaleDateString() : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "tax" && taxData && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold text-[#0A0A0A]">Tax Year {taxData.tax_year}</h4>
              <Calendar className="h-4 w-4 text-[#bbb]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-[#FDFCF9] border border-[#f0ece4]">
                <p className="text-[9px] uppercase tracking-wider text-[#888] font-bold">Interest Income</p>
                <p className="text-lg font-bold text-[#0A0A0A]">{fmtUSD(taxData.total_interest_income)}</p>
              </div>
              <div className="p-3 rounded-xl bg-[#FDFCF9] border border-[#f0ece4]">
                <p className="text-[9px] uppercase tracking-wider text-[#888] font-bold">Principal Returned</p>
                <p className="text-lg font-bold text-[#0A0A0A]">{fmtUSD(taxData.total_principal_returned)}</p>
              </div>
              <div className="p-3 rounded-xl bg-[#FDFCF9] border border-[#f0ece4]">
                <p className="text-[9px] uppercase tracking-wider text-[#888] font-bold">Total Invested</p>
                <p className="text-lg font-bold text-[#0A0A0A]">{fmtUSD(taxData.total_invested)}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                <p className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold">Total Distributions</p>
                <p className="text-lg font-bold text-emerald-700">{fmtUSD(taxData.total_distributions)}</p>
              </div>
            </div>
            <p className="text-[10px] text-[#999] leading-relaxed italic">{taxData.note}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Fund Updates Panel ───────────────────────────────────────────

export function FundUpdatesPanel() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ic_token");

  useEffect(() => {
    axios.get(`${API}/fund-portal/fund-updates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUpdates(r.data.updates || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-6"><Loader2 className="h-4 w-4 text-emerald-600 animate-spin" /></div>;
  if (updates.length === 0) return null;

  const typeColors = {
    quarterly: "bg-blue-50 text-blue-700 border-blue-200",
    performance: "bg-emerald-50 text-emerald-700 border-emerald-200",
    capital_call: "bg-amber-50 text-amber-700 border-amber-200",
    distribution: "bg-purple-50 text-purple-700 border-purple-200",
    general: "bg-gray-50 text-gray-700 border-gray-200",
    monthly_summary: "bg-indigo-50 text-indigo-700 border-indigo-200",
  };

  return (
    <div style={{ borderRadius: "var(--ic-radius-lg)", border: "1px solid var(--ic-border)", background: "var(--ic-bg-card)", overflow: "hidden", boxShadow: "var(--ic-shadow-sm)" }} data-testid="fund-updates-panel">
      <div style={{ padding: "14px 20px", borderBottom: "2px solid var(--ic-gold)", display: "flex", alignItems: "center", gap: 8 }}>
        <Send style={{ width: 16, height: 16, color: "var(--ic-green-dark)" }} />
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ic-green-dark)" }}>Fund Updates</span>
        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "rgba(64,145,108,0.1)", color: "var(--ic-status-open)" }}>{updates.length}</span>
      </div>
      <div style={{ maxHeight: 400, overflowY: "auto" }}>
        {updates.map((u, idx) => (
          <div key={u.update_id} style={{ padding: "12px 16px", borderBottom: idx < updates.length - 1 ? "1px solid var(--ic-border)" : "none", transition: "background 0.15s ease" }}
            onMouseOver={(e) => e.currentTarget.style.background = "var(--ic-bg-row-alt)"}
            onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
            data-testid={`fund-update-${u.update_id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <span className="ic-status-badge" style={{
                fontSize: 8, padding: "2px 6px",
                color: u.update_type === "monthly_summary" ? "var(--ic-green-dark)" : u.update_type === "quarterly" ? "var(--ic-status-pending)" : "var(--ic-text-secondary)",
                background: u.update_type === "monthly_summary" ? "rgba(27,67,50,0.1)" : u.update_type === "quarterly" ? "rgba(217,119,6,0.1)" : "rgba(156,163,175,0.1)",
              }}>
                {u.update_type.replace(/_/g, " ")}
              </span>
              <span style={{ fontSize: 9, color: "var(--ic-text-secondary)" }}>{u.sent_at ? new Date(u.sent_at).toLocaleDateString() : ""}</span>
            </div>
            <h4 style={{ fontSize: 11, fontWeight: 700, color: "var(--ic-text-primary)", marginBottom: 4, lineHeight: 1.4 }}>{u.title}</h4>
            <p style={{ fontSize: 10, color: "var(--ic-text-secondary)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", margin: 0 }}
              dangerouslySetInnerHTML={{ __html: u.content_html?.replace(/<[^>]*>/g, ' ').substring(0, 120) }} />
          </div>
        ))}
      </div>
    </div>
  );
}
